import { uploadToS3 } from './awsS3UploadService';
import { FOOTAGE_LAYOUT_STITCH } from './footageStitch';

const VALID_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/ogg'];

export function isValidMatchVideoFile(file) {
  if (!file) return false;
  const fileType = file.type || '';
  const fileName = file.name || '';
  const hasValidExtension = /\.(mp4|mov|avi|webm|ogv)$/i.test(fileName);
  return VALID_VIDEO_TYPES.includes(fileType) || hasValidExtension || fileType.startsWith('video/');
}

export function isStitchFootageLayout(footageLayout) {
  return footageLayout === FOOTAGE_LAYOUT_STITCH || footageLayout === 'stitch';
}

export function footageFilesToValidate({
  footageLayout,
  footageFile,
  footageFileRight,
  stitchFiles,
} = {}) {
  if (isStitchFootageLayout(footageLayout)) {
    return Array.isArray(stitchFiles) ? stitchFiles.filter(Boolean) : [];
  }
  return [footageFile, footageFileRight].filter(Boolean);
}

export function shouldEnqueueFootageUpload({
  customLinkType,
  footageLayout,
  footageFile,
  stitchFiles,
} = {}) {
  if (customLinkType !== 'upload') return false;
  if (isStitchFootageLayout(footageLayout)) {
    return (Array.isArray(stitchFiles) ? stitchFiles.filter(Boolean).length : 0) >= 2;
  }
  return Boolean(footageFile);
}

export function getFootageLayoutValidationReason({
  footageFile,
  footageFileRight,
  footageLayout,
  stitchFiles,
  allowMissing = false,
} = {}) {
  if (isStitchFootageLayout(footageLayout)) {
    const parts = Array.isArray(stitchFiles) ? stitchFiles.filter(Boolean) : [];
    if (allowMissing && parts.length === 0) return null;
    if (parts.length < 2) {
      return 'Add at least two video clips, in order, so we can stitch them into one match video.';
    }
    return null;
  }
  if (allowMissing && !footageFile && !footageFileRight) return null;
  if (!footageFile) return 'Please upload your match video.';
  if (footageLayout === 'dual' && !footageFileRight) {
    return 'Please upload both the left and right camera videos.';
  }
  return null;
}

export function footageMetaForDualMp4(rightUrl) {
  const url = rightUrl || null;
  return {
    dual_mp4: Boolean(url),
    game_video_link_2: url,
  };
}

export function pendingDirectUploadMeta(footageLayout = 'single') {
  const stitch = isStitchFootageLayout(footageLayout);
  return {
    footage_upload_pending: true,
    dual_mp4: footageLayout === 'dual',
    stitch_mp4: stitch,
    footage_stitch_pending: stitch,
    game_video_link_2: null,
  };
}

/**
 * Upload left (required unless omitted) and optional right-side pitch MP4s.
 * Progress is combined across both files (0–100).
 */
export async function uploadPitchFootagePair({
  leftFile,
  rightFile,
  matchId,
  onProgress,
  allowAnonymous = false,
}) {
  const parts = [leftFile, rightFile].filter(Boolean);
  if (parts.length === 0) {
    throw new Error('Please upload match footage.');
  }

  let completed = 0;
  const report = (partFraction, details) => {
    if (!onProgress) return;
    onProgress(Math.min(100, Math.round(((completed + partFraction) / parts.length) * 100)), details);
  };

  let leftUrl = null;
  let rightUrl = null;

  if (leftFile) {
    const result = await uploadToS3(
      leftFile,
      matchId,
      (progress, details) => report(progress / 100, details),
      allowAnonymous,
    );
    leftUrl = result.videoUrl;
    if (!leftUrl) {
      throw new Error('Left-side video uploaded but no playback URL was returned.');
    }
    completed += 1;
    report(0);
  }

  if (rightFile) {
    const result = await uploadToS3(
      rightFile,
      matchId,
      (progress, details) => report(progress / 100, details),
      allowAnonymous,
    );
    rightUrl = result.videoUrl;
    if (!rightUrl) {
      throw new Error('Right-side video uploaded but no playback URL was returned.');
    }
    completed += 1;
    report(0);
  }

  if (onProgress) onProgress(100);
  return { leftUrl, rightUrl };
}
